import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { Authenticator } from '@aws-amplify/ui-react'
import { Amplify } from 'aws-amplify'
import { HelmetProvider } from 'react-helmet-async'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import config from './amplifyconfiguration.json'
import store, { persistor } from 'redux/store'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'
import './index.css'
import 'i18n/index' // Initialize i18n before app renders
import { CookieStorage } from 'aws-amplify/utils'
import { App } from 'App'
import { datadogService, getTelemetryConfig } from 'lib/insights'

Amplify.configure(config)

cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new CookieStorage({
    domain: import.meta.env.VITE_COOKIE_DOMAIN,
    expires: 30, // 30 days (CookieStorage expects days, not milliseconds)
    secure: import.meta.env.VITE_COOKIE_SECURE === 'true',
  })
)

// Initialize Datadog RUM before rendering
const telemetryConfig = getTelemetryConfig()

if (telemetryConfig?.datadog?.enabled) {
  datadogService.init(telemetryConfig.datadog).catch((error: Error) => {
    console.error('[Main] Failed to initialize Datadog:', error)
  })

  // Stop session and replay recording when tab is closed
  window.addEventListener('beforeunload', () => {
    if (datadogService.isInitialized()) {
      console.log('[Datadog] beforeunload - stopping session')
      datadogService.stopSession()
    }
  })

  // Also handle visibility change (when tab is hidden/minimized)
  document.addEventListener('visibilitychange', () => {
    if (
      document.visibilityState === 'hidden' &&
      datadogService.isInitialized()
    ) {
      console.log('[Datadog] Tab hidden - stopping session')
      datadogService.stopSession()
    }
  })
}

const rootElement = document.getElementById('root')
if (rootElement) {
  createRoot(rootElement).render(
    <StrictMode>
      <BrowserRouter>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <HelmetProvider>
              <Authenticator.Provider>
                <App />
              </Authenticator.Provider>
            </HelmetProvider>
          </PersistGate>
        </Provider>
      </BrowserRouter>
    </StrictMode>
  )
}
